<mat-card class="query-card">
  <mat-card-content>
    @if (!hasSubmitted) {
    <form [formGroup]="queryForm" (ngSubmit)="submitQuery()" class="query-form">
      <mat-form-field
        appearance="outline"
        class="w-100 query-editor-field"
        [class.query-field-pulse]="isQueryFieldPulsing"
      >
        <mat-label>Read-only SQL query</mat-label>
        <textarea
          #queryEditor
          matInput
          formControlName="query"
          rows="5"
          spellcheck="false"
          class="monospace"
        ></textarea>
      </mat-form-field>

      <div class="query-form-footer">
        <div class="actions">
          <button
            mat-stroked-button
            type="button"
            class="plan-query-button"
            [disabled]="queryForm.invalid || isPlanning"
            (click)="planQuery()"
          >
            <mat-icon>{{ isPlanning ? 'hourglass_empty' : 'account_tree' }}</mat-icon>
            {{ queryPlan && isPlanStale() ? 'Update Plan' : 'Plan Query' }}
          </button>
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="queryForm.invalid || isSubmitting"
          >
            <mat-icon>play_arrow</mat-icon>
            Launch Query
          </button>
        </div>

        <div class="editor-link-row">
          <details class="timestamp-guidance">
            <summary>
              <mat-icon aria-hidden="true">info_outline</mat-icon>
              Timestamp filter guidance
            </summary>
            <div class="timestamp-guidance-panel">
              <p>
                For indexed <code>obs_sbn.obstime</code> filters, type the date as a
                timestamp:
              </p>
              <pre><code>WHERE obstime &gt;= TIMESTAMP '2026-06-01 00:00:00'</code></pre>
              <p>Avoid applying casts or functions to <code>obstime</code> itself.</p>
            </div>
          </details>
          <a
            class="schema-link"
            href="https://docs.minorplanetcenter.net/mpc-ops-docs/data-and-services/replicated-tables-schema/"
            target="_blank"
            rel="noopener"
          >
            Browse schema reference
          </a>
        </div>
      </div>

      <p class="query-limits-text">{{ queryLimitsLabel() }}</p>
    </form>

    @if (isPlanning || planErrorMessage || queryPlan) {
    <section class="plan-panel" aria-label="Estimated query plan">
      <div class="plan-panel-header">
        <div>
          <h3>Estimated Plan</h3>
          @if (queryPlan && isPlanStale()) {
          <p class="plan-stale-text">
            Plan is for an earlier version of this query.
          </p>
          }
        </div>
        @if (isPlanning) {
        <span class="plan-status" role="status" aria-live="polite">Planning query...</span>
        }
      </div>

      @if (planErrorMessage) {
      <div class="plan-error" role="alert">
        {{ planErrorMessage }}
      </div>
      }

      @if (queryPlan; as planResult) {
      <div class="plan-metrics" [class.plan-stale]="isPlanStale()">
        <div class="plan-metric">
          <div class="plan-metric-heading">
            <span>Total cost</span>
            <button
              type="button"
              class="plan-metric-info"
              [matTooltip]="planMetricHelp.totalCost"
              matTooltipPosition="above"
              matTooltipClass="plan-help-tooltip"
              aria-label="Explain total cost"
            >
              <mat-icon aria-hidden="true">info_outline</mat-icon>
            </button>
          </div>
          <strong>{{ planMetricLabel(planResult.summary.total_cost, 2) }}</strong>
        </div>
        <div class="plan-metric">
          <div class="plan-metric-heading">
            <span>Estimated rows</span>
            <button
              type="button"
              class="plan-metric-info"
              [matTooltip]="planMetricHelp.estimatedRows"
              matTooltipPosition="above"
              matTooltipClass="plan-help-tooltip"
              aria-label="Explain estimated rows"
            >
              <mat-icon aria-hidden="true">info_outline</mat-icon>
            </button>
          </div>
          <strong>{{ planMetricLabel(planResult.summary.estimated_rows) }}</strong>
        </div>
        <div class="plan-metric">
          <div class="plan-metric-heading">
            <span>Plan nodes</span>
            <button
              type="button"
              class="plan-metric-info"
              [matTooltip]="planMetricHelp.nodeCount"
              matTooltipPosition="above"
              matTooltipClass="plan-help-tooltip"
              aria-label="Explain plan nodes"
            >
              <mat-icon aria-hidden="true">info_outline</mat-icon>
            </button>
          </div>
          <strong>{{ planMetricLabel(planResult.summary.node_count) }}</strong>
        </div>
        <div class="plan-metric">
          <div class="plan-metric-heading">
            <span>Max depth</span>
            <button
              type="button"
              class="plan-metric-info"
              [matTooltip]="planMetricHelp.maxDepth"
              matTooltipPosition="above"
              matTooltipClass="plan-help-tooltip"
              aria-label="Explain maximum plan depth"
            >
              <mat-icon aria-hidden="true">info_outline</mat-icon>
            </button>
          </div>
          <strong>{{ planMetricLabel(planResult.summary.max_depth) }}</strong>
        </div>
      </div>

      @if (planResult.summary.node_types.length || planResult.summary.relations.length) {
      <div class="plan-chip-groups">
        @if (planResult.summary.node_types.length) {
        <div class="plan-chip-group">
          <span class="plan-chip-label">Node types</span>
          <div class="plan-chips">
            <span class="plan-chip" *ngFor="let type of planResult.summary.node_types; trackBy: trackChip">
              {{ type }}
            </span>
          </div>
        </div>
        }
        @if (planResult.summary.relations.length) {
        <div class="plan-chip-group">
          <span class="plan-chip-label">Relations</span>
          <div class="plan-chips">
            <span class="plan-chip relation-chip" *ngFor="let relation of planResult.summary.relations; trackBy: trackChip">
              {{ relation }}
            </span>
          </div>
        </div>
        }
      </div>
      }

      @if (planResult.summary.warnings.length) {
      <section class="plan-warnings" aria-label="Query plan warnings">
        <div class="plan-section-title">
          <mat-icon aria-hidden="true">warning</mat-icon>
          <h4>Warnings</h4>
        </div>
        <ul>
          <li *ngFor="let warning of planResult.summary.warnings; let warningIndex = index; trackBy: trackWarning">
            <strong>{{ warning.message }}</strong>
            <span>{{ warning.node_type }}{{ warning.relation_name ? ' · ' + warning.relation_name : '' }}</span>
          </li>
        </ul>
      </section>
      }

      <section class="plan-tree-section" aria-label="Plan tree">
        <div class="plan-section-title">
          <mat-icon aria-hidden="true">schema</mat-icon>
          <h4>Plan Tree</h4>
        </div>
        <ng-container
          *ngTemplateOutlet="planNodeTemplate; context: { $implicit: planResult.plan }"
        ></ng-container>
      </section>

      <details class="raw-plan-details">
        <summary>
          <mat-icon aria-hidden="true">data_object</mat-icon>
          Raw Plan
        </summary>
        <pre>{{ rawPlanJson() }}</pre>
      </details>
      }
    </section>
    }

    <section class="preset-section">
      <div class="preset-header">
        <h3>Preset Queries</h3>
      </div>

      <div class="example-queries">
        @for (example of exampleQueries; track example.buttonLabel) {
        <button
          mat-stroked-button
          color="accent"
          type="button"
          [attr.title]="example.buttonLabel + ' - ' + example.sql"
          [class.example-query-active]="activeExampleQuery === example.sql"
          (click)="applyExample(example.sql)"
        >
          <div class="example-label">{{ example.buttonLabel }}</div>
          <div class="example-sql">{{ example.sql }}</div>
        </button>
        }
      </div>
    </section>
    } @else if (querySubmission) {
    <div class="submission-details mat-elevation-z1">
      <table>
        <tr
          [class.highlight]="
            isPendingStatus(querySubmission.status) || isPartialStatus(querySubmission.status)
          "
          [class.status-error-row]="querySubmission.status === 'error'"
          [class.status-partial-row]="isPartialStatus(querySubmission.status)"
        >
          <td><strong>Status:</strong></td>
          <td
            [class.status-success-text]="querySubmission.status === 'success'"
            [class.status-partial-text]="isPartialStatus(querySubmission.status)"
          >
            {{ querySubmission.status }}
          </td>
        </tr>
        <tr>
          <td><strong>Job ID:</strong></td>
          <td>{{ querySubmission.job_id || 'Assigning…' }}</td>
        </tr>
        @if (processingStartedLabel(querySubmission)) {
        <tr>
          <td><strong>Processing Started:</strong></td>
          <td>{{ processingStartedLabel(querySubmission) }}</td>
        </tr>
        }
        @if (queueTimeLabel(querySubmission)) {
        <tr>
          <td><strong>Queue Time:</strong></td>
          <td>{{ queueTimeLabel(querySubmission) }}</td>
        </tr>
        }
        @if (processingTimeLabel(querySubmission)) {
        <tr>
          <td><strong>Processing Time:</strong></td>
          <td>{{ processingTimeLabel(querySubmission) }}</td>
        </tr>
        }
        @if (shouldShowStreamedRows(querySubmission)) {
        <tr>
          <td><strong>Rows Streamed:</strong></td>
          <td>{{ streamedRowsLabel(querySubmission) }}</td>
        </tr>
        }
        @if (shouldShowResultMessage(querySubmission)) {
        <tr>
          <td><strong>Progress:</strong></td>
          <td>{{ querySubmission.result_message }}</td>
        </tr>
        }
        @if (streamingProgressDetail(querySubmission)) {
        <tr>
          <td><strong>Batch Progress:</strong></td>
          <td>{{ streamingProgressDetail(querySubmission) }}</td>
        </tr>
        }
        <tr>
          <td><strong>Query:</strong></td>
          <td>{{ querySubmission.query }}</td>
        </tr>
        <tr>
          <td><strong>Creation Time:</strong></td>
          <td>{{ formatCreationTime(querySubmission.creation_time) }}</td>
        </tr>
        @if (querySubmission.error_message) {
        <tr>
          <td><strong>Error Message:</strong></td>
          <td>{{ querySubmission.error_message }}</td>
        </tr>
        }
      </table>
      @if (querySubmission.status === 'error') {
      <div class="actions results-actions">
        <button mat-stroked-button type="button" (click)="clearResults()">Clear</button>
      </div>
      }
    </div>
    }
  </mat-card-content>
</mat-card>

<ng-template #planNodeTemplate let-node>
  <details class="plan-node" open>
    <summary>
      <span class="plan-node-title">{{ planNodeTitle(node) }}</span>
      <span
        class="plan-node-meta"
        tabindex="0"
        [matTooltip]="planNodeCostHelp"
        matTooltipPosition="above"
        matTooltipClass="plan-help-tooltip"
      >
        cost {{ planCostRange(node) }} · rows {{ planMetricLabel(node.estimated_rows) }}
        <mat-icon aria-hidden="true">info_outline</mat-icon>
      </span>
    </summary>

    @if (hasNodeDetails(node)) {
    <dl class="plan-node-details">
      @if (node.join_type) {
      <div>
        <dt>Join</dt>
        <dd>{{ node.join_type }}</dd>
      </div>
      }
      @if (node.index_name) {
      <div>
        <dt>Index</dt>
        <dd>{{ node.index_name }}</dd>
      </div>
      }
      @if (node.hash_condition) {
      <div>
        <dt>Hash condition</dt>
        <dd>{{ node.hash_condition }}</dd>
      </div>
      }
      @if (node.merge_condition) {
      <div>
        <dt>Merge condition</dt>
        <dd>{{ node.merge_condition }}</dd>
      </div>
      }
      @if (node.index_condition) {
      <div>
        <dt>Index condition</dt>
        <dd>{{ node.index_condition }}</dd>
      </div>
      }
      @if (node.filter) {
      <div>
        <dt>Filter</dt>
        <dd>{{ node.filter }}</dd>
      </div>
      }
    </dl>
    }

    @if (node.children?.length) {
    <div class="plan-node-children">
      <ng-container *ngFor="let child of node.children || []">
        <ng-container
          *ngTemplateOutlet="planNodeTemplate; context: { $implicit: child }"
        ></ng-container>
      </ng-container>
    </div>
    }
  </details>
</ng-template>
